import { useMemo } from 'react'

import { matchFilterArrayIncludes, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

const watchEntityKeys = ['statuses']

type StatusesFilters = {
  status_keys?: string[],
  string?: string,
}

interface Options {
  deps?: any[],
  entityKey?: string,
  filters?: StatusesFilters,
  performHttpRequests?: boolean,
}

function useStatuses(options: Options = {}) {
  const { deps = [], entityKey, filters = {} } = options
  const {
    status_keys: filterStatusKeys = [],
    string: filterString,
  } = filters

  const {
    updatedEntities: { statuses: statusesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const statuses = useSelector(reduxState => reduxState.entities.statuses)

  const filteredStatuses = useMemo(() => {
    const filtered = Object.values(statuses).filter((status) => {
      const { id, key, title } = status

      const matchTitle: boolean = matchFilterString(title, filterString)
      const matchId: boolean = id?.toString() === filterString
      const matchString: boolean = matchId || matchTitle

      const matchStatusKeys = filterStatusKeys.length ? matchFilterArrayIncludes(filterStatusKeys, key) : true

      return matchStatusKeys && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [statusesUpdatedAt, JSON.stringify(filters)])

  const filteredStatusesCount = filteredStatuses.length
  const hasFilteredStatuses = !!filteredStatusesCount

  const loadMorePayload = useLoadMore({
    loadedCount: filteredStatusesCount,
    filters,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingStatuses } = useReduxAction(
    'statuses',
    'loadStatuses',
    {
      entityKey,
      ...filtersWithOffset,
      limit,
    },
    [filters, performHttpRequests, ...deps],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredStatuses,
    filteredStatusesCount,
    hasFilteredStatuses,
    loading: loadingStatuses,
  }
}

export default useStatuses
